import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Star, Search, TrendingUp, LayoutGrid, List, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [ciudadFilter, setCiudadFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [formData, setFormData] = useState({
    nombre: '', empresa: '', whatsapp: '', ciudad: '', etiquetas: [], estado: 'Activo', notas: '', rating: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [ciudadesUnicas, setCiudadesUnicas] = useState([]);
  const pageSize = 10;
  const serverChunkSize = 100;

  const etiquetasDisponibles = ['SUVs', 'Premium', 'Interior', 'Ticket alto', 'Compactos', 'Clásicos'];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1);
      setClientes([]);
      loadClientesChunk(0);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, estadoFilter, ciudadFilter]);

  useEffect(() => {
    const requiredStartIndex = (currentPage - 1) * pageSize;
    if (requiredStartIndex >= clientes.length && clientes.length < totalCount) {
      loadClientesChunk(clientes.length);
    }
  }, [currentPage]);

  const loadClientesChunk = async (skip) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/clientes/paginated`, {
        params: {
          skip,
          limit: serverChunkSize,
          searchTerm: searchTerm || undefined,
          estadoFilter: estadoFilter !== 'all' ? estadoFilter : undefined,
          ciudadFilter: ciudadFilter !== 'all' ? ciudadFilter : undefined,
          sortField: 'nombre',
          sortDirection: 'asc'
        }
      });
      const { items, total, ciudades } = res.data;
      setTotalCount(total);
      
      if (skip === 0) {
        setClientes(items);
        setCiudadesUnicas(ciudades);
      } else {
        setClientes(prev => {
          const newIds = items.map(i => i.id);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id));
          return [...filteredPrev, ...items];
        });
      }
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const refreshFullState = () => {
    loadClientesChunk(0);
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingId(cliente.id);
      setFormData(cliente);
    } else {
      setEditingId(null);
      setFormData({ nombre: '', empresa: '', whatsapp: '', ciudad: '', etiquetas: [], estado: 'Activo', notas: '', rating: null });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.nombre) {
        toast.error('El nombre es requerido');
        return;
      }
      if (editingId) {
        await axios.put(`${API}/clientes/${editingId}`, formData);
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clientes`, formData);
        toast.success('Cliente creado');
      }
      setShowDialog(false);
      refreshFullState();
    } catch (error) {
      console.error('Error saving cliente:', error);
      toast.error('Error al guardar cliente');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/clientes/${id}`);
      toast.success('Cliente movido a papelera');
      refreshFullState();
    } catch (error) {
      console.error('Error deleting cliente:', error);
      toast.error('Error al eliminar cliente');
    }
    setDeleteId(null);
  };

  const toggleEtiqueta = (etiqueta) => {
    const current = formData.etiquetas || [];
    if (current.includes(etiqueta)) {
      setFormData({ ...formData, etiquetas: current.filter((e) => e !== etiqueta) });
    } else {
      setFormData({ ...formData, etiquetas: [...current, etiqueta] });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Clientes</h1>
              <p className="text-muted-foreground mt-1">{totalCount} clientes encontrados</p>
            </div>
            <div className="flex gap-2">
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => handleOpenDialog()} data-testid="dealer-new-button">
                <Plus className="h-4 w-4 mr-2" />Nuevo Cliente
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, empresa o ciudad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-dealer-input"
              />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="estado-dealer-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Activo">Activo</SelectItem>
                <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                <SelectItem value="bloqueado_wa">Bloqueados por WA</SelectItem>
              </SelectContent>
            </Select>
            <Select value={ciudadFilter} onValueChange={setCiudadFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="ciudad-filter">
                <SelectValue placeholder="Ciudad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las ciudades</SelectItem>
                {ciudadesUnicas.map((ciudad) => (
                  <SelectItem key={ciudad} value={ciudad}>
                    {ciudad}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {totalCount === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              No hay clientes registrados o que coincidan con la búsqueda
            </p>
          </Card>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="grid gap-4">
                {clientes.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((cliente) => (
                  <Card key={cliente.id} className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{cliente.nombre}</h3>
                        {cliente.empresa && <p className="text-sm text-muted-foreground">{cliente.empresa}</p>}
                      </div>
                      <Badge variant={cliente.estado === 'Activo' ? 'default' : 'destructive'}>{cliente.estado}</Badge>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm mb-3">
                      {cliente.whatsapp && (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">WhatsApp:</span> 
                          <span>{cliente.whatsapp}</span>
                          {cliente.whatsappBloqueado && (
                            <AlertTriangle className="h-4 w-4 text-destructive drop-shadow-sm" title="Número bloqueado temporalmente por API de WhatsApp (Ecosistema)" />
                          )}
                        </div>
                      )}
                      {cliente.ciudad && <div><span className="text-muted-foreground">Ciudad:</span> {cliente.ciudad}</div>}
                      {cliente.rating && (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Rating:</span>
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`h-3 w-3 ${i < cliente.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" />
                        <span className="text-muted-foreground">Ofertas:</span>
                        <span className="font-semibold">{cliente.ofertasCount || 0}</span>
                        <span className="text-muted-foreground text-xs">
                          ({cliente.ofertasAceptadasCount || 0} aceptadas)
                        </span>
                      </div>
                    </div>
                    {cliente.etiquetas && cliente.etiquetas.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {cliente.etiquetas.map((tag) => (
                          <Badge key={tag} variant="outline">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenDialog(cliente)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setDeleteId(cliente.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-4 font-medium">Nombre</th>
                    <th className="text-left p-4 font-medium">Empresa</th>
                    <th className="text-left p-4 font-medium">Ciudad</th>
                    <th className="text-left p-4 font-medium">Ofertas</th>
                    <th className="text-left p-4 font-medium">Rating</th>
                    <th className="text-left p-4 font-medium">Estado</th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {clientes.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((cliente) => (
                    <tr key={cliente.id} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="p-4">
                        <div className="font-medium">{cliente.nombre}</div>
                        {cliente.whatsapp && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            📱 {cliente.whatsapp}
                            {cliente.whatsappBloqueado && (
                              <AlertTriangle className="h-3.5 w-3.5 text-destructive drop-shadow-sm ml-1" title="Número bloqueado temporalmente por API de WhatsApp (Ecosistema)" />
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-sm">{cliente.empresa || '-'}</td>
                      <td className="p-4 text-sm">{cliente.ciudad || '-'}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-primary" />
                          <span className="font-semibold">{cliente.ofertasCount || 0}</span>
                          <span className="text-xs text-muted-foreground">
                            ({cliente.ofertasAceptadasCount || 0} ✓)
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        {cliente.rating && (
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${i < cliente.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge variant={cliente.estado === 'Activo' ? 'default' : 'destructive'}>
                          {cliente.estado}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleOpenDialog(cliente)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setDeleteId(cliente.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
            )}
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / pageSize)}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Cliente</DialogTitle>
            <DialogDescription>Completa la información del cliente</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input id="nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="empresa">Empresa</Label>
                <Input id="empresa" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input id="whatsapp" placeholder="+598 99 123 456" value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ciudad">Ciudad</Label>
                <Input id="ciudad" value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Etiquetas</Label>
              <div className="flex flex-wrap gap-2">
                {etiquetasDisponibles.map((tag) => (
                  <Badge
                    key={tag}
                    variant={(formData.etiquetas || []).includes(tag) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleEtiqueta(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estado">Estado</Label>
                <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Activo">Activo</SelectItem>
                    <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating">Rating (1-5)</Label>
                <Select value={formData.rating?.toString() || ''} onValueChange={(value) => setFormData({ ...formData, rating: parseInt(value) })}>
                  <SelectTrigger><SelectValue placeholder="Sin rating" /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((r) => <SelectItem key={r} value={r.toString()}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notas">Notas</Label>
              <Textarea id="notas" rows={3} value={formData.notas} onChange={(e) => setFormData({ ...formData, notas: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="dealer-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>El cliente será movido a la papelera. Su nombre seguirá visible en las ofertas existentes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clientes;